import 'bootstrap-datepicker';
import 'bootstrap-datepicker/js/locales/bootstrap-datepicker.pl';

/**
 * Wybór dnia rankingu dziennego.
 *
 * Kalendarz dopuszcza wyłącznie dni, które API wymieniło w `days` — dla dowolnej innej daty
 * `/api/ranking/daily` odpowiada 404, więc nie ma sensu pozwalać jej wybrać. Lista dni
 * przychodzi razem z pierwszą odpowiedzią, dlatego pole składamy dopiero wtedy (`setDays`),
 * a nie na montażu strony.
 *
 * Ten sam kalendarz (bootstrap-datepicker) obsługuje kalendarz laureatów i ma już style
 * w `components/datepicker.scss`.
 */
export class DateField {
    /**
     * @param {HTMLInputElement} input
     * @param {(date: string) => void} onChange - wywoływane z datą ISO (YYYY-MM-DD)
     */
    constructor(input, onChange) {
        this.input = input;
        this.onChange = onChange;
        this.days = [];
        this.value = null;
        this.initialized = false;
    }

    /**
     * @param {Array<{date: string}>} days - dni rankingu, rosnąco
     * @param {string} selected - dzień pokazywany w polu, ISO
     */
    setDays(days, selected) {
        this.days = days.map((day) => day.date).filter(Boolean);
        this.value = selected;

        if (!this.days.length) {
            this.input.disabled = true;

            return;
        }

        this.input.disabled = false;

        if (this.initialized) {
            $(this.input).datepicker('update', fromIsoDate(selected));

            return;
        }

        const allowed = new Set(this.days);

        $(this.input)
            .datepicker({
                format: 'dd.mm.yyyy',
                // Daty podajemy jako obiekty Date: łańcuchy kalendarz parsowałby wg `format`,
                // a nasze przychodzą z API w ISO.
                startDate: fromIsoDate(this.days[0]),
                endDate: fromIsoDate(this.days[this.days.length - 1]),
                // Dni bez rankingu (przerwy w konkursie) zostają nieklikalne
                beforeShowDay: (date) => allowed.has(toIsoDate(date)),
                keyboardNavigation: false,
                weekStart: 1,
                language: 'pl',
                autoclose: true,
                todayHighlight: false,
            })
            .on('changeDate', (event) => {
                const iso = toIsoDate(event.date);

                if (iso === this.value) {
                    return;
                }

                this.value = iso;
                this.onChange(iso);
            })
            .datepicker('update', fromIsoDate(selected));

        this.initialized = true;
    }

    /** Dzień obok wybranego — `step` -1/+1. Zwraca null, gdy jesteśmy na skraju konkursu. */
    neighbour(step) {
        const index = this.days.indexOf(this.value);

        if (index === -1) {
            return null;
        }

        return this.days[index + step] ?? null;
    }

    select(date) {
        this.value = date;
        $(this.input).datepicker('update', fromIsoDate(date));
    }
}

/** YYYY-MM-DD → data lokalna. `new Date(iso)` dałoby północ UTC, czyli czasem dzień wcześniej. */
function fromIsoDate(iso) {
    const [year, month, day] = iso.split('-').map(Number);

    return new Date(year, month - 1, day);
}

/** Data lokalna → YYYY-MM-DD. `toISOString()` przeliczyłby na UTC i cofnął dzień. */
function toIsoDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
}
